import {
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardCode,
  KeyboardSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { pluralize } from '@racemap/utilities/formattings/formatting';
import { createContext, type FC, type PropsWithChildren, useContext, useState } from 'react';
import { Preview } from '../BasicComponents/DragLayer';
import {
  arrowKeyCoordinateGetter,
  type FilterDropItem,
  RowPointerSensor,
  type TrackerDropItem,
  toDropItem,
} from './dnd';

/**
 * A dragged table row is as wide as the table, so an area-based hit test would favour whichever
 * panel it overlaps most rather than what the cursor is on. Follow the pointer instead, and fall
 * back to centre distance for keyboard drags, which have no pointer.
 */
const collisionDetection: CollisionDetection = (args) =>
  args.pointerCoordinates == null ? closestCenter(args) : pointerWithin(args);

/** Takes the drag-start snapshot: dnd-kit empties `active.data` once the dragged row unmounts. */
const deliver = (item: TrackerDropItem | FilterDropItem | null, { over }: DragEndEvent) => {
  const target = over?.data.current;
  if (item == null || target == null || !Array.isArray(target.accept)) return;
  if (!target.accept.includes(item.itemType)) return;

  if (typeof target.onDrop === 'function') target.onDrop(item);
};

const DraggedItemContext = createContext<TrackerDropItem | FilterDropItem | null>(null);

/**
 * What is being dragged right now, or null. Changes only when a drag starts or ends, so reading
 * it does not tie a component to the pointer. Safe outside the provider — the Tracker Manager's
 * mobile layout renders the same tables without any drag context.
 */
export const useDraggedItem = () => useContext(DraggedItemContext);

const renderPreview = (item: TrackerDropItem | FilterDropItem) => {
  const count = item.itemType === 'filter-tree' ? item.data.count : item.data.ids.size;
  return (
    <Preview>
      {count} {pluralize(count, 'Tracker')}
    </Preview>
  );
};

/**
 * Hosts every Tracker Manager drag: table rows and filter-tree nodes as sources, tables, tags and
 * event nodes as targets. One context, so a row can be dropped anywhere in the layout.
 */
export const TrackerDndProvider: FC<PropsWithChildren> = ({ children }) => {
  const sensors = useSensors(
    // Without a distance threshold every click on a row would register as a drag.
    useSensor(RowPointerSensor, { activationConstraint: { distance: 5 } }),
    // Space belongs to the table, where it selects the focused row, so enter carries the drag.
    useSensor(KeyboardSensor, {
      coordinateGetter: arrowKeyCoordinateGetter,
      keyboardCodes: {
        start: [KeyboardCode.Enter],
        end: [KeyboardCode.Enter, KeyboardCode.Tab],
        cancel: [KeyboardCode.Esc],
      },
    }),
  );

  // Held as state rather than read from dnd-kit's context so neither the preview nor anything
  // reading useDraggedItem re-renders on pointer moves; DragOverlay follows the cursor itself.
  const [dragged, setDragged] = useState<TrackerDropItem | FilterDropItem | null>(null);

  const handleDragStart = ({ active }: DragStartEvent) =>
    setDragged(toDropItem(active.data.current));

  const handleDragEnd = (event: DragEndEvent) => {
    setDragged(null);
    deliver(dragged, event);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragged(null)}
    >
      <DraggedItemContext.Provider value={dragged}>{children}</DraggedItemContext.Provider>
      {/* snapCenterToCursor: the overlay would otherwise sit on the dragged row's own box,
          which is as wide as the table. dropAnimation off — react-dnd had none. */}
      <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null}>
        {dragged != null && renderPreview(dragged)}
      </DragOverlay>
    </DndContext>
  );
};
